import { call, put, select } from 'redux-saga/effects';
import { callWithAuth } from '../../sagaApiHelper';
import { createCampaignApi } from '../../../api/apiLists/createCampaignApi';
import { runCampaignApi } from '../../../api/apiLists/runCampaignApi';
import { uploadBuffer } from '../fileUploadSaga';
import { showAlert } from '../../alertSlice';
import { startSending, sendingSuccess, sendingError } from '../../slices/sendMessageSlice';
import { getOrFetchWabaId } from './utils';
import {
  getTemplateHeaderMediaType,
  parseS3FileUrl,
  uploadCampaignTemplateMedia,
} from './campaignMediaHelper';

export function* handleCreateCampaign(action) {
  try {
    yield put(startSending());
    const { csv, fileName, variableMapping, templateId, executionMode = 'NOW' } = action.payload || {};

    if (!csv) {
      yield put(showAlert({ message: 'No CSV content provided for campaign.', type: 'error' }));
      yield put(sendingError('No CSV content'));
      return;
    }

    const companyInfo = yield select(state => state.companyInfo?.info || {});
    const companyId = yield select(state => state.auth?.user?.companyId || companyInfo?.companyId || companyInfo?.id || '');
    const createdBy = yield select(state => state.auth?.user?.email || state.auth?.user?.username || state.auth?.user?.id || '');
    const selectedPhone = yield select(state => state.whatsapp.selectedPhone || {});
    const templates = yield select(state => state.whatsapp.templates || []);
    const sendMediaFile = yield select(state => state.sendMessage?.sendMediaFile || null);
    const selectedTemplate = templates.find(t => String(t.id) === String(templateId)) || {};

    const wabaIdFromStorage = yield call(getOrFetchWabaId);
    const wabaId = wabaIdFromStorage || companyInfo?.wabaId || companyInfo?.whatsappBusinessAccountId || '';

    if (!wabaId) {
      yield put(showAlert({ message: 'WhatsApp Business Account (WABA) ID not found', type: 'error' }));
      yield put(sendingError('WABA ID not found'));
      return;
    }

    const mediaType = getTemplateHeaderMediaType(selectedTemplate);

    const mediaUpload = yield call(uploadCampaignTemplateMedia, {
      sendMediaFile,
      companyId,
      wabaId,
      displayPhoneNumberId: selectedPhone?.id,
    });

    if (!mediaUpload.ok) {
      yield put(showAlert({ message: mediaUpload.alertMessage, type: 'error' }));
      yield put(sendingError(mediaUpload.failureReason));
      return;
    }

    const mediaId = mediaUpload.mediaId;

    const uploadFileName = fileName || `campaign-${Date.now()}.csv`;
    const uploadResult = yield call(uploadBuffer, {
      fileName: uploadFileName,
      contentType: 'text/csv',
      data: csv,
      customerId: companyId,
      operation: 'whatsapp-campaign',
    });
    if (!uploadResult || !uploadResult.fileURL) {
      yield put(showAlert({ message: 'Failed to upload CSV for campaign', type: 'error' }));
      yield put(sendingError('CSV upload failed'));
      return;
    }

    const { bucket, key } = parseS3FileUrl(uploadResult.fileURL);

    const campaignReq = {
      operation: 'createCampaign',
      payload: {
        companyId,
        name: selectedTemplate?.name,
        category: selectedTemplate?.category,
        channel: 'WHATSAPP',
        channelAccountId: wabaId,
        execute: { mode: executionMode, scheduledAt: null },
        createdBy,
        audience: { source: 'S3', bucket, key },
        channelConfig: {
          WHATSAPP: {
            wabaId,
            sender: {
              senderNumber: selectedPhone?.number,
              displayPhoneNumberId: selectedPhone?.id
            },
            template: {
              id: selectedTemplate?.id,
              name: selectedTemplate?.name,
              language: selectedTemplate?.language
            },
            media: {
              type: mediaType,
              assetId: mediaId
            }
          }
        },
        variableMapping: mediaId ? { ...variableMapping, 'header': { 'id': mediaId } } : variableMapping
      }
    };

    const res = yield call(callWithAuth, createCampaignApi, campaignReq);

    if (res && (res.status === true || String(res.status).toUpperCase() === 'SUCCESS' || String(res.status).toUpperCase() === 'QUEUED' || String(res.status).toUpperCase() === 'PUBLISHED')) {

      if (String(res.status).toUpperCase() === 'PUBLISHED' && res.campaignId) {
        const runCampaignReq = {
          operation: 'runCampaign',
          payload: {
            campaignId: res.campaignId,
            mode: executionMode
          }
        };

        const runRes = yield call(callWithAuth, runCampaignApi, runCampaignReq);

        if (runRes && (runRes.status === "OK" || String(runRes.status).toUpperCase() === 'SUCCESS')) {
          // yield put(showAlert({ message: `Message processing started successfully.`, type: 'success' }));
          yield put(sendingSuccess());
        } else {
          yield put(showAlert({ message: runRes?.errMsg || 'Campaign created but failed to start', type: 'warning' }));
          yield put(sendingSuccess());
        }
      } else {
        yield put(showAlert({ message: `Campaign created successfully.`, type: 'success' }));
        yield put(sendingSuccess());
      }
    } else {
      yield put(showAlert({ message: res?.errMsg || 'Failed to create campaign', type: 'error' }));
      yield put(sendingError('Campaign create failed'));
    }

  } catch (error) {
    yield put(showAlert({ message: 'Error occurred while creating campaign', type: 'error' }));
    yield put(sendingError('Campaign create failed'));
  }
}
